import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import {
  Grid,
  Typography,
  Button,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
} from '@mui/material';

import {
  LocalizationProvider,
} from '@mui/x-date-pickers/LocalizationProvider';

import {
  AdapterDayjs,
} from '@mui/x-date-pickers/AdapterDayjs';

import {
  DatePicker,
} from '@mui/x-date-pickers/DatePicker';

import dayjs from 'dayjs';

import { AuthContext } from 'src/context/AuthContext';

import SubmitDwrModal from './submit_dwr_modal';

export default function StaffDwrForm({
  register,
  errors,
  getValues,
  setValue,
  isEdit,
  handleSubmit,
  onSubmit,
  params,
  loading,
  control,
  watch,
  taskdata,
  costItemData,
  setcostItemData,
  categoryData,
  editdata,
  attachments,
  setAttachments,
  signOne,
  setSignOne,
  signTwo,
  setSignTwo,
  mySignature,
  setMySignature,
  PRIVATE_ROUTE,
  authAxios,
  joballData,
  jobIdValue,
  setJobIdValue,
  setLoading,
  availableWork,
  availableJobs,
  availableTasks,
  selectedWork,
  setSelectedWork,
}) {
  const { state } = useContext(AuthContext);

  const navigate = useNavigate();

  const [condition, setCondition] = useState(
    params ? editdata?.task_name : ''
  );

  const selectedDate = watch('task_date');
  const selectedJobId = watch('job_id');
  const selectedTaskId = watch('task_id');

  /*
   * ============================================================
   * Helper functions
   * ============================================================
   */

  const getCostItem = (costItem) => {
    if (Array.isArray(costItem)) {
      return costItem.join(', ');
    }

    return costItem || '-';
  };

  const getDate = (date) => {
    return date ? String(date).substring(0, 10) : '-';
  };

  /*
   * ============================================================
   * Employee Name
   * ============================================================
   */

  const getEmployeeName = (employeeId, employee) => {
    if (employee?.first_name || employee?.last_name) {
      return `${employee?.first_name || ''} ${employee?.last_name || ''
        }`.trim();
    }

    if (employee?.name) {
      return employee.name;
    }

    if (!employeeId) {
      return '-';
    }

    return String(employeeId);
  };

  const getAssignees = (assignedMembers) => {
    if (!assignedMembers?.length) {
      return '-';
    }

    return assignedMembers
      .map((member) =>
        getEmployeeName(
          member?.employee,
          member?.employee_data ||
          member?.employee_details ||
          member?.user
        )
      )
      .filter(Boolean)
      .join(', ') || '-';
  };

  /*
   * ============================================================
   * Normalize available work
   * ============================================================
   */

  const normalizedAvailableWork = useMemo(() => {
    if (!Array.isArray(availableWork)) {
      return [];
    }

    return availableWork;
  }, [availableWork]);

  /*
   * ============================================================
   * Job list
   * ============================================================
   */

  const jobOptions = useMemo(() => {
    const jobMap = new Map();

    const addJob = (jobId, jobNumber, taskNumber) => {
      if (
        jobId === undefined ||
        jobId === null ||
        String(jobId).trim() === ''
      ) {
        return;
      }

      const id = String(jobId);

      if (!jobMap.has(id)) {
        jobMap.set(id, {
          id,
          label: jobNumber || id,
          task_number: taskNumber || '',
        });
      }
    };

    normalizedAvailableWork.forEach((item) => {
      addJob(
        item?.job_id,
        item?.job_number,
        item?.task_number
      );

      addJob(
        item?.schedule?.job_id,
        item?.schedule?.job_number,
        item?.schedule?.task_number
      );

      addJob(
        item?.todoDraft?.job_id,
        item?.todoDraft?.job_number,
        item?.todoDraft?.task_number
      );

      if (Array.isArray(item?.schedule?.todoDrafts)) {
        item.schedule.todoDrafts.forEach((draft) => {
          addJob(
            draft?.job_id ||
            item?.schedule?.job_id,
            draft?.job_number ||
            item?.schedule?.job_number,
            draft?.task_number ||
            item?.schedule?.task_number
          );
        });
      }
    });

    return Array.from(jobMap.values());
  }, [normalizedAvailableWork]);

  /*
   * ============================================================
   * Keep jobIdValue synchronized
   * ============================================================
   */

  useEffect(() => {
    if (selectedJobId) {
      setJobIdValue(selectedJobId);
    } else {
      setJobIdValue('');
    }
  }, [
    selectedJobId,
    setJobIdValue,
  ]);

  /*
   * ============================================================
   * Task list
   * ============================================================
   */

  const taskOptions = useMemo(() => {
    if (!selectedJobId) {
      return [];
    }

    const taskMap = new Map();

    const addTask = (
      taskId,
      taskNumber,
      taskName
    ) => {
      if (
        taskId === undefined ||
        taskId === null ||
        String(taskId).trim() === ''
      ) {
        return;
      }

      const id = String(taskId);

      if (!taskMap.has(id)) {
        taskMap.set(id, {
          id,
          label:
            taskNumber ||
            taskName ||
            id,
        });
      }
    };

    normalizedAvailableWork.forEach((item) => {
      const itemJobId =
        item?.job_id ??
        item?.schedule?.job_id ??
        item?.todoDraft?.job_id;

      if (
        String(itemJobId) !==
        String(selectedJobId)
      ) {
        return;
      }

      addTask(
        item?.task_id,
        item?.task_number,
        item?.task_name
      );

      addTask(
        item?.schedule?.task_id,
        item?.schedule?.task_number,
        item?.schedule?.task_name
      );

      addTask(
        item?.todoDraft?.task_id,
        item?.todoDraft?.task_number,
        item?.todoDraft?.task_name
      );

      if (Array.isArray(item?.schedule?.todoDrafts)) {
        item.schedule.todoDrafts.forEach((draft) => {
          addTask(
            draft?.task_id,
            draft?.task_number ||
            item?.schedule?.task_number,
            draft?.task_name
          );
        });
      }
    });

    return Array.from(taskMap.values());
  }, [
    normalizedAvailableWork,
    selectedJobId,
  ]);

  /*
   * ============================================================
   * Reset task when Job changes
   * ============================================================
   */

  useEffect(() => {
    if (!selectedJobId) {
      setValue('task_id', '');
      setValue('task_name', '');
      return;
    }

    setValue('task_id', '');
    setValue('task_name', '');
  }, [
    selectedJobId,
    setValue,
  ]);

  /*
   * ============================================================
   * Selected available work
   * ============================================================
   */

  const selectedAvailableWork = useMemo(() => {
    if (
      !selectedJobId ||
      !selectedTaskId
    ) {
      return [];
    }

    const result = [];

    normalizedAvailableWork.forEach((item) => {
      const itemJobId =
        item?.job_id ??
        item?.schedule?.job_id ??
        item?.todoDraft?.job_id;

      if (
        String(itemJobId) !==
        String(selectedJobId)
      ) {
        return;
      }

      if (
        item?.type === 'schedule' &&
        String(item?.task_id) ===
        String(selectedTaskId)
      ) {
        result.push(item);
        return;
      }

      if (
        item?.type === 'todoDraft' &&
        String(item?.task_id) ===
        String(selectedTaskId)
      ) {
        result.push(item);
        return;
      }

      if (item?.schedule) {
        const schedule = item.schedule;

        if (
          String(schedule?.task_id) ===
          String(selectedTaskId)
        ) {
          result.push({
            type: 'schedule',
            schedule,
            todoDraft: null,
          });
        }

        if (
          Array.isArray(schedule?.todoDrafts)
        ) {
          schedule.todoDrafts
            .filter(
              (draft) =>
                String(draft?.task_id) ===
                String(selectedTaskId)
            )
            .forEach((draft) => {
              result.push({
                type: 'todoDraft',
                schedule,
                todoDraft: draft,
              });
            });
        }
      }

      if (
        item?.todoDraft &&
        String(item?.todoDraft?.task_id) ===
        String(selectedTaskId)
      ) {
        result.push(item);
      }
    });

    const unique = [];
    const seen = new Set();

    result.forEach((item) => {
      const id =
        item?.schedule?._id ||
        item?.todoDraft?._id ||
        item?._id;

      const type =
        item?.type || 'work';

      const key = `${type}-${id}`;

      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    });

    return unique;
  }, [
    normalizedAvailableWork,
    selectedJobId,
    selectedTaskId,
  ]);

  /*
   * ============================================================
   * Date change
   * ============================================================
   */

  const handleDateChange = (newValue) => {
    const formattedDate =
      newValue &&
        newValue.isValid()
        ? newValue.format('YYYY-MM-DD')
        : '';

    setValue(
      'task_date',
      formattedDate,
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    setValue('job_id', '');
    setValue('job', '');
    setValue('task_id', '');
    setValue('task_name', '');

    setJobIdValue('');

    setSelectedWork(null);
  };

  /*
   * ============================================================
   * Job change
   * ============================================================
   */

  const handleJobChange = (event) => {
    const value =
      event?.target?.value || '';

    setValue(
      'job_id',
      value,
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    const selectedJob =
      jobOptions.find(
        (job) =>
          String(job.id) ===
          String(value)
      );

    setValue(
      'job',
      selectedJob?.label || value,
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    setJobIdValue(value);

    setValue('task_id', '');
    setValue('task_name', '');

    setSelectedWork(null);
  };

  /*
   * ============================================================
   * Task change
   * ============================================================
   */

  const handleTaskChange = (event) => {
    const value =
      event?.target?.value || '';

    setValue(
      'task_id',
      value,
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    const selectedTask =
      taskOptions.find(
        (task) =>
          String(task.id) ===
          String(value)
      );

    setValue(
      'task_name',
      selectedTask?.label || value,
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );

    setSelectedWork(null);
  };

  /*
   * ============================================================
   * Render
   * ============================================================
   */

  return (
    <form
      autoComplete="off"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid
        container
        spacing={3}
        padding={3}
      >
        {/* =====================================================
            Page Title
        ====================================================== */}

        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h2"
          >
            {params
              ? 'Edit DWR Report'
              : 'Create DWR Report'}
          </Typography>
        </Grid>

        {/* =====================================================
            Field Date / Job / Task
        ====================================================== */}

        {!params && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 2,
                  sm: 2.5,
                },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor:
                  'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                  columnGap: {
                    xs: 0,
                    md: 2.5,
                  },
                  rowGap: {
                    xs: 1,
                    md: 2.5,
                  },
                }}
              >
                {/* =============================================
              FIELD DATE
            ============================================== */}

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <FormLabel
                    sx={{
                      display: 'block',
                      margin: 0,
                      padding: 0,
                      mb: 0.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.5,
                    }}
                  >
                    Field Date / Requested
                  </FormLabel>

                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                  >
                    <DatePicker
                      value={
                        selectedDate
                          ? dayjs(selectedDate)
                          : null
                      }
                      disableFuture
                      onChange={handleDateChange}
                      renderInput={(inputParams) => (
                        <TextField
                          {...inputParams}
                          fullWidth
                          required
                          size="small"
                          placeholder="Select date"
                          error={Boolean(
                            errors?.task_date
                          )}
                          helperText={
                            errors?.task_date?.message ||
                            undefined
                          }
                          FormHelperTextProps={{
                            sx: {
                              margin: 0,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <input
                    type="hidden"
                    {...register('task_date', {
                      required:
                        'Field Date / Requested is required',
                    })}
                  />
                </Box>

                {/* =============================================
              JOB
            ============================================== */}

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <FormLabel
                    sx={{
                      display: 'block',
                      margin: 0,
                      padding: 0,
                      mb: 0.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.5,
                    }}
                  >
                    Job
                  </FormLabel>

                  {!selectedDate ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value=""
                      placeholder="Select date first"
                    />
                  ) : jobOptions.length === 0 ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value=""
                      placeholder="No available jobs"
                    />
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={selectedJobId || ''}
                      onChange={handleJobChange}
                      SelectProps={{
                        native: true,
                      }}
                      error={Boolean(
                        errors?.job_id
                      )}
                      helperText={
                        errors?.job_id?.message ||
                        undefined
                      }
                      FormHelperTextProps={{
                        sx: {
                          margin: 0,
                        },
                      }}
                    >
                      <option value="">
                        Select Job
                      </option>

                      {jobOptions.map((job) => (
                        <option
                          key={job.id}
                          value={job.id}
                        >
                          {job.label}
                        </option>
                      ))}
                    </TextField>
                  )}
                </Box>

                {/* =============================================
                  TASK
                ============================================== */}

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <FormLabel
                    sx={{
                      display: 'block',
                      margin: 0,
                      padding: 0,
                      mb: 0.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.5,
                    }}
                  >
                    Task
                  </FormLabel>

                  {!selectedJobId ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value=""
                      placeholder="Select job first"
                    />
                  ) : taskOptions.length === 0 ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value=""
                      placeholder="No available tasks"
                    />
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={selectedTaskId || ''}
                      onChange={handleTaskChange}
                      SelectProps={{
                        native: true,
                      }}
                      error={Boolean(
                        errors?.task_id
                      )}
                      helperText={
                        errors?.task_id?.message ||
                        undefined
                      }
                      FormHelperTextProps={{
                        sx: {
                          margin: 0,
                        },
                      }}
                    >
                      <option value="">
                        Select Task
                      </option>

                      {taskOptions.map((task) => (
                        <option
                          key={task.id}
                          value={task.id}
                        >
                          {task.label}
                        </option>
                      ))}
                    </TextField>
                  )}
                </Box>
              </Box>

              {/* ===============================================
                  NO AVAILABLE WORK MESSAGE
              ================================================ */}

              {selectedDate &&
                jobOptions.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: '1px solid',
                      borderColor:
                        'divider',
                    }}
                  >
                    No Available work items
                    available for this{' '}
                    {selectedDate} for you
                  </Typography>
                )}
            </Paper>
          </Grid>
        )}

        {/* =====================================================
            Available Work
        ====================================================== */}

        {!params &&
          selectedJobId &&
          selectedTaskId && (
            <Grid
              item
              xs={12}
              sx={{ mt: 0 }}
            >
              <Typography
                variant="h6"
                component="h2"
                mb={2}
              >
                Available Work
              </Typography>

              {selectedAvailableWork.length ===
                0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No available scheduled
                  work found for this
                  Job and Task.
                </Typography>
              ) : (
                <>
                  {/* ==========================================
                      DESKTOP / TABLET VIEW
                  =========================================== */}

                  <Box
                    sx={{
                      display: {
                        xs: 'none',
                        sm: 'block',
                      },
                    }}
                  >
                    <TableContainer
                      component={Paper}
                      sx={{
                        width: '100%',
                        overflowX: 'auto',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor:
                          'divider',
                        boxShadow: 'none',
                      }}
                    >
                      <Table
                        sx={{
                          minWidth: 950,
                        }}
                      >
                        <TableHead>
                          <TableRow
                            sx={{
                              '& th': {
                                fontWeight: 600,
                                whiteSpace:
                                  'nowrap',
                                backgroundColor:
                                  'background.neutral',
                              },
                            }}
                          >
                            <TableCell>
                              Field Date
                            </TableCell>

                            <TableCell>
                              Job
                            </TableCell>

                            <TableCell>
                              Task
                            </TableCell>

                            <TableCell>
                              Cost Item
                            </TableCell>

                            <TableCell>
                              Assignee
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                width: 150,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {selectedAvailableWork.map(
                            (item) => {
                              const schedule =
                                item?.schedule;

                              const draft =
                                item?.todoDraft;

                              const isTodoDraft =
                                item?.type ===
                                'todoDraft';

                              const work =
                                isTodoDraft
                                  ? draft
                                  : schedule;

                              const jobNumber =
                                item?.job_number ??
                                work?.job_number ??
                                schedule?.job_number ??
                                draft?.job_number ??
                                '';

                              const taskId =
                                item?.task_id ??
                                work?.task_id ??
                                schedule?.task_id ??
                                draft?.task_id;

                              const taskNumber =
                                item?.task_number ??
                                work?.task_number ??
                                schedule?.task_number ??
                                draft?.task_number ??
                                taskId;

                              const fieldDate =
                                isTodoDraft
                                  ? work?.field_trip_date
                                  : work?.planned_date;

                              return (
                                <TableRow
                                  key={
                                    work?._id ||
                                    item?._id
                                  }
                                  hover
                                  sx={{
                                    '& td': {
                                      verticalAlign:
                                        'middle',
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      whiteSpace:
                                        'nowrap',
                                    }}
                                  >
                                    {getDate(
                                      fieldDate
                                    )}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      whiteSpace:
                                        'nowrap',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {jobNumber || '-'}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      whiteSpace:
                                        'nowrap',
                                    }}
                                  >
                                    {taskNumber ||
                                      '-'}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      minWidth: 220,
                                    }}
                                  >
                                    {getCostItem(
                                      work?.cost_item
                                    )}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      minWidth: 160,
                                    }}
                                  >
                                    {getAssignees(
                                      work?.assigned_members
                                    )}
                                  </TableCell>

                                  <TableCell
                                    align="right"
                                  >
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => {
                                        setSelectedWork({
                                          type: isTodoDraft
                                            ? 'todoDraft'
                                            : 'schedule',

                                          schedule,
                                          todoDraft: isTodoDraft
                                            ? draft
                                            : null,

                                          job_number:
                                            item?.job_number ||
                                            work?.job_number ||
                                            '',

                                          task_number:
                                            item?.task_number ||
                                            work?.task_number ||
                                            '',
                                        });
                                      }}
                                    >
                                      Submit DWR
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* ==========================================
                      MOBILE VIEW
                  =========================================== */}

                  <Box
                    sx={{
                      display: {
                        xs: 'block',
                        sm: 'none',
                      },
                    }}
                  >
                    {selectedAvailableWork.map(
                      (item) => {
                        const schedule =
                          item?.schedule;

                        const draft =
                          item?.todoDraft;

                        const isTodoDraft =
                          item?.type ===
                          'todoDraft';

                        const work =
                          isTodoDraft
                            ? draft
                            : schedule;

                        return (
                          <React.Fragment
                            key={
                              work?._id ||
                              item?._id
                            }
                          >
                            <Paper
                              elevation={1}
                              sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{
                                  mb: 1.5,
                                  lineHeight: 1.4,
                                }}
                              >
                                {getCostItem(
                                  work?.cost_item
                                )}
                              </Typography>

                              <Grid
                                container
                                spacing={1.5}
                              >
                                <Grid
                                  item
                                  xs={6}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Field Date/Requested
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                  >
                                    {getDate(
                                      isTodoDraft
                                        ? work?.field_trip_date
                                        : work?.planned_date
                                    )}
                                  </Typography>
                                </Grid>

                                <Grid
                                  item
                                  xs={6}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Assignee
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                      wordBreak:
                                        'break-word',
                                    }}
                                  >
                                    {getAssignees(
                                      work?.assigned_members
                                    )}
                                  </Typography>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                >
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                    }}
                                    onClick={() => {
                                      setSelectedWork({
                                        type: isTodoDraft
                                          ? 'todoDraft'
                                          : 'schedule',

                                        schedule,
                                        todoDraft: isTodoDraft
                                          ? draft
                                          : null,

                                        job_number:
                                          item?.job_number ||
                                          work?.job_number ||
                                          '',

                                        task_number:
                                          item?.task_number ||
                                          work?.task_number ||
                                          '',
                                      });
                                    }}
                                  >
                                    Submit DWR
                                  </Button>
                                </Grid>
                              </Grid>
                            </Paper>
                          </React.Fragment>
                        );
                      }
                    )}
                  </Box>
                </>
              )}
            </Grid>
          )}

        {/* =====================================================
            Existing Submit DWR Modal
        ====================================================== */}

        <SubmitDwrModal
          open={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          selectedWork={selectedWork}
          jobNumber={selectedWork?.job_number || ''}
          taskNumber={selectedWork?.task_number || ''}
          onDwrSubmitted={() => {
            setSelectedWork(null);
            navigate(-1);
          }}
        />
      </Grid>
    </form>
  );
}

StaffDwrForm.propTypes = {
  register: PropTypes.any,
  params: PropTypes.any,
  errors: PropTypes.object,
  handleSubmit: PropTypes.any,
  onSubmit: PropTypes.func,
  control: PropTypes.any,
  value: PropTypes.any,
  handleChange: PropTypes.func,
  getValues: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,

  taskdata: PropTypes.array,
  costItemData: PropTypes.array,
  setcostItemData: PropTypes.func,
  categoryData: PropTypes.array,

  editdata: PropTypes.object,

  attachments: PropTypes.any,
  setAttachments: PropTypes.func,

  signOne: PropTypes.any,
  setSignOne: PropTypes.func,

  signTwo: PropTypes.any,
  setSignTwo: PropTypes.func,

  mySignature: PropTypes.any,
  setMySignature: PropTypes.func,

  PRIVATE_ROUTE: PropTypes.object,
  authAxios: PropTypes.object,

  joballData: PropTypes.array,

  jobIdValue: PropTypes.any,
  setJobIdValue: PropTypes.func,

  setLoading: PropTypes.func,

  availableWork: PropTypes.array,

  availableJobs: PropTypes.array,

  availableTasks: PropTypes.array,

  selectedWork: PropTypes.any,

  setSelectedWork: PropTypes.func,
};